import apiClient from './apiClient';

export interface TrialInfo {
  is_in_trial: boolean;
  trial_cycles_remaining: number;
  trial_price: number;
  regular_price: number;
}

export interface SubscriptionStatus {
  subscription_tier: string;
  subscription_status: string;
  paypal_subscription_id?: string;
  paypal_order_id?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  next_billing_date?: string; // Next billing cycle date
  cancelled_date?: string; // When subscription was cancelled
  cancellation_reason?: string; // Reason for cancellation
  is_complimentary: boolean;
  complimentary_reason?: string;
  billing_cycle?: string; // 'monthly' or 'annual'
    downgrade_pending?: boolean; // Whether a downgrade is scheduled for next billing cycle
    pending_tier?: string; // Tier that will be active after downgrade completes
    pending_plan_id?: string; // Plan ID that will be active after downgrade completes
    pending_billing_cycle?: string; // Billing cycle for pending downgrade ('monthly' or 'annual')
  free_upgrade_available_monthly?: boolean; // Free upgrade available for Starter Monthly → Pro Monthly
  free_upgrade_available_annual?: boolean; // Free upgrade available for Starter Annual → Pro Annual
  features?: Record<string, boolean>; // Dynamic features from database
  trial_info?: TrialInfo; // Trial period information if subscription is in trial
  user_roles?: string[]; // List of active role names for role-based tier determination
}

export interface UploadLimits {
  subscription_tier: string;
  daily_upload_limit?: number; // Deprecated, use instant_limit
  current_upload_count: number; // Deprecated, use instant_count + scheduled_count
  remaining_uploads?: number; // Deprecated
  allow_scheduled_uploads: boolean;
  allow_upload_defaults: boolean;
  reset_time_utc: string;
  reset_countdown_seconds: number;
  features: Record<string, boolean>; // Dynamic features from database
  // Separate instant and scheduled upload counts
  instant_count: number;
  instant_limit?: number; // null/undefined means unlimited
  scheduled_count: number;
  scheduled_limit?: number; // null/undefined means unlimited
}

export interface Features extends Record<string, boolean | string | number | undefined> {
  subscription_tier: string;
  daily_upload_limit?: number;
  max_accounts?: number;
  is_complimentary: boolean;
  // All other feature flags are dynamic from database
}

export interface SubscriptionEvent {
  id: number;
  date: string;
  event_type: string;
  event_source: string;
  description?: string;
  tier?: string;
  previous_tier?: string;
  status?: string;
  previous_status?: string;
  billing_cycle?: string;
  amount?: string;
  currency?: string;
  initiated_by?: string;
}

export interface SubscriptionHistoryResponse {
  events: SubscriptionEvent[];
  total: number;
  limit: number;
  offset: number;
}

export const paymentService = {
  getSubscriptionStatus: async (): Promise<SubscriptionStatus> => {
    const response = await apiClient.get('/payment/subscription/status');
    return response.data.data;
  },

  getUploadLimits: async (): Promise<UploadLimits> => {
    const response = await apiClient.get('/payment/subscription/upload-limits');
    return response.data.data;
  },

  getActiveSubscriptionPlans: async (): Promise<{
    unified_plans: Array<{
      plan_name: string;
      description: string | null;
      monthly_plans: Record<string, {
        id: number;
        paypal_plan_id: string;
        tier: string;
        billing_cycle: string;
        price: number | null;
        base_price: number | null;
        discounted_price: number | null;
        discount_percentage: number | null;
        twelve_x_monthly_price: number | null;
        annual_savings_percentage: number | null;
        currency_code: string;
        description: string | null;
        paypal_plan_name: string;
        discount_cause: string | null;
      }>;
      annual_plans: Record<string, {
        id: number;
        paypal_plan_id: string;
        tier: string;
        billing_cycle: string;
        price: number | null;
        base_price: number | null;
        discounted_price: number | null;
        discount_percentage: number | null;
        twelve_x_monthly_price: number | null;
        annual_savings_percentage: number | null;
        currency_code: string;
        description: string | null;
        paypal_plan_name: string;
        discount_cause: string | null;
      }>;
    }>;
    paypal_client_id: string;
    total: number;
  }> => {
    const response = await apiClient.get('/payment/subscription-plans/active');
    return response.data.data;
  },

  getSubscriptionLimits: async (tier: string): Promise<{
    tier: string;
    daily_instant_upload_limit: number | null;
    daily_scheduled_upload_limit: number | null;
    allow_scheduled_uploads: boolean;
    allow_upload_defaults: boolean;
    features: Record<string, boolean>; // Dynamic features from database
  }> => {
    const response = await apiClient.get(`/payment/subscription-limits/${tier}`);
    return response.data.data;
  },

  createPendingSubscription: async (paypalPlanId: string): Promise<{
    session_token: string;
    expires_at: string;
    paypal_plan_id: string;
    tier: string;
    billing_cycle: string;
  }> => {
    const response = await apiClient.post('/subscriptions/pending', {
      paypal_plan_id: paypalPlanId
    });
    return response.data.data;
  },

  reportSubscriptionProcessingError: async (errorData: {
    paypal_subscription_id?: string;
    paypal_order_id?: string;
    plan_id: string;
    error_type: string;
    details: any;
  }): Promise<void> => {
    await apiClient.post('/payment/subscription/processing-error', errorData);
  },

  getUpgradeProration: async (newPlanId: string): Promise<{
    prorated_amount: string;
    old_tier: string;
    new_tier: string;
    old_price: string;
    new_price: string;
    old_billing_cycle?: string;
    new_billing_cycle?: string;
    days_remaining: number;
    total_days: number;
    billing_cycle_end_date: string | null;
    currency: string;
    prorated_new_plan_charge: string;
    prorated_old_plan_credit: string;
  }> => {
    const response = await apiClient.get('/payment/subscription/upgrade/proration', {
      params: { new_plan_id: newPlanId }
    });
    return response.data.data;
  },

  upgradeSubscription: async (newPlanId: string): Promise<{
    subscription_id: string;
    old_tier: string;
    new_tier: string;
    prorated_amount: string;
    approval_url?: string;
    requires_approval: boolean;
    requires_proration?: boolean;
    proration_approval_url?: string;
    proration_order_id?: string;
  }> => {
    const response = await apiClient.post('/payment/subscription/upgrade', {
      new_plan_id: newPlanId
    });
    return response.data.data;
  },

  downgradeSubscription: async (newPlanId: string): Promise<{
    subscription_id: string;
    old_tier: string;
    new_tier: string;
    approval_url?: string;
    requires_approval: boolean;
    message: string;
  }> => {
    const response = await apiClient.post('/payment/subscription/downgrade', {
      new_plan_id: newPlanId
    });
    return response.data.data;
  },

  freeUpgradeToPro: async (): Promise<{
    subscription_id: string;
    old_tier: string;
    new_tier: string;
    approval_url?: string;
    requires_approval: boolean;
  }> => {
    const response = await apiClient.post('/payment/subscription/free-upgrade');
    return response.data.data;
  },

  syncSubscription: async (): Promise<void> => {
    const response = await apiClient.post('/payment/subscription/sync');
    return response.data.data;
  },

  cancelSubscription: async (reason?: string): Promise<{
    subscription_id?: string;
    tier?: string;
    approval_url?: string;
    cancel_url?: string;
    requires_approval?: boolean;
  }> => {
    const response = await apiClient.post('/payment/subscription/cancel', { reason });
    return response.data.data;
  },

  /**
   * Verify subscription activation by querying PayPal API directly.
   * Called after user returns from PayPal approval to get immediate feedback.
   */
  verifySubscriptionActivation: async (paypalSubscriptionId: string): Promise<{
    status: 'active' | 'pending' | 'failed';
    subscription_tier?: string;
    billing_cycle?: string;
    next_billing_date?: string;
    message?: string;
  }> => {
    const response = await apiClient.post('/payment/subscription/verify-activation', {
      paypal_subscription_id: paypalSubscriptionId
    });
    return response.data.data;
  },

  /**
   * Log PayPal flow events for debugging subscription activation issues.
   * Called at key points in the PayPal subscription flow to track what happened.
   */
  logPayPalFlowEvent: async (eventData: {
    event_type: 'flow_started' | 'onApprove_fired' | 'verify_success' | 'verify_error' | 'incomplete_flow_detected';
    subscription_id?: string;
    session_token?: string;
    plan_id?: string;
    tier?: string;
    billing_cycle?: string;
    error_message?: string;
    metadata?: Record<string, any>;
  }): Promise<void> => {
    try {
      await apiClient.post('/payment/subscription/log-flow-event', eventData);
    } catch (error) {
      // Silently fail - logging errors shouldn't break the flow
      console.error('[PayPal] Failed to log flow event:', error);
    }
  },

  // ==========================================================================
  // Lifetime Access APIs
  // ==========================================================================

  /**
   * Get current lifetime access availability and pricing (public endpoint).
   * Use this for landing page - does not include user-specific data.
   */
  getLifetimeStatus: async (): Promise<{
    available: boolean;
    current_tier: number | null;
    current_price: number | null;
    original_price: number | null;
    discount_percentage: number | null;
    slots_remaining: number;
    total_sold: number;
    user_has_lifetime: boolean;
    user_purchase_number: number | null;
    user_purchase_date: string | null;
  }> => {
    const response = await apiClient.get('/payment/lifetime/status');
    return response.data.data;
  },

  /**
   * Get current lifetime access availability and pricing (authenticated endpoint).
   * Use this for subscriptions page - includes whether user owns lifetime.
   */
  getLifetimeStatusAuthenticated: async (): Promise<{
    available: boolean;
    current_tier: number | null;
    current_price: number | null;
    original_price: number | null;
    discount_percentage: number | null;
    slots_remaining: number;
    total_sold: number;
    user_has_lifetime: boolean;
    user_purchase_number: number | null;
    user_purchase_date: string | null;
  }> => {
    const response = await apiClient.get('/payment/lifetime/status/me');
    return response.data.data;
  },

  /**
   * Create a PayPal order for lifetime access purchase.
   */
  createLifetimeOrder: async (): Promise<{
    order_id: string;
    approval_url: string;
    price: number;
    tier_number: number;
    currency: string;
  }> => {
    const response = await apiClient.post('/payment/lifetime/create-order');
    return response.data.data;
  },

  /**
   * Capture a lifetime access order after PayPal approval.
   */
  captureLifetimeOrder: async (orderId: string): Promise<{
    success: boolean;
    purchase_number: number | null;
    tier: string | null;
    role: string | null;
    price_paid: number | null;
    message: string | null;
  }> => {
    const response = await apiClient.post('/payment/lifetime/capture-order', {
      order_id: orderId
    });
    return response.data.data;
  },

  /**
   * Get subscription history timeline for the current user.
   */
  getSubscriptionHistory: async (limit: number = 50, offset: number = 0): Promise<SubscriptionHistoryResponse> => {
    const response = await apiClient.get('/payment/subscription/history', {
      params: { limit, offset }
    });
    return response.data;
  },
};

